import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Router, NavigationCancel, NavigationEnd } from '@angular/router';
import { Location, LocationStrategy, PathLocationStrategy } from '@angular/common';
import { filter, takeUntil, catchError } from 'rxjs/operators';
import { Subject, of } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { PaymentsService } from 'src/app/services/payments.service';
import { ToastrService } from 'ngx-toastr';

@Component({
    selector: 'app-navbar',
    templateUrl: './navbar.component.html',
    styleUrls: ['./navbar.component.scss'],
    providers: [
        Location, {
            provide: LocationStrategy,
            useClass: PathLocationStrategy
        }
    ]
})
export class NavbarComponent implements OnInit, OnDestroy {

    location: any;
    routerSubscription: any;
    isScrolling = false;
    isAuthenticated = false;
    userEmail = '';
    userName = '';
    userInitials = '';
    userPlan = 'Plan Invitado';
    showProfileMenu = false;
    private destroy$ = new Subject<void>();

    constructor(
        private router: Router, 
        private authService: AuthService, 
        private paymentsService: PaymentsService,
        private toastr: ToastrService
    ) {
    }

    ngOnInit(){
        this.recallJsFuntions();
        
        // Suscribirse a los cambios de estado de autenticación
        this.authService.authState$
            .pipe(takeUntil(this.destroy$))
            .subscribe(isAuthenticated => {
                this.isAuthenticated = isAuthenticated;
                if (isAuthenticated) {
                    this.checkUserPlan();
                } else {
                    this.userPlan = 'Plan Invitado';
                }
            });

        // Suscribirse a los cambios del email del usuario
        this.authService.userEmail$
            .pipe(takeUntil(this.destroy$))
            .subscribe(email => {
                this.userEmail = email;
            });

        // Suscribirse a los cambios del nombre del usuario
        this.authService.userName$
            .pipe(takeUntil(this.destroy$))
            .subscribe(name => {
                this.userName = name;
                this.updateUserInitials();
            });

        // Suscribirse a los cambios de ruta para actualizar el plan
        this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                if (this.isAuthenticated) {
                    this.checkUserPlan();
                }
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }


    showLogoutConfirm = false;

    onLogoutClick(): void {
        this.showLogoutConfirm = true;
    }

    confirmLogout(): void {
        try {
            this.authService.logout();
            this.showLogoutConfirm = false;
            this.toastr.success('Has cerrado sesión exitosamente', 'Sesión cerrada', {
                timeOut: 3000,
                progressBar: true,
                closeButton: true
            });
            this.router.navigate(['/']);
        } catch (error) {
            console.error('Error al cerrar sesión:', error);
            this.showLogoutConfirm = false;
            this.toastr.error('Ocurrió un error al cerrar sesión', 'Error', {
                timeOut: 3000,
                progressBar: true,
                closeButton: true
            });
        }
    }

    cancelLogout(): void {
        this.showLogoutConfirm = false;
    }

    // Métodos para el menú de perfil
    toggleProfileMenu(): void {
        this.showProfileMenu = !this.showProfileMenu;
    }

    closeProfileMenu(): void {
        this.showProfileMenu = false;
    }

    updateUserInitials(): void {
        this.userInitials = this.authService.getUserInitials();
    }

    // Método para obtener solo el primer nombre
    getUserDisplayName(): string {
        if (this.userName) {
            const names = this.userName.trim().split(' ');
            // Retorna solo el primer nombre
            return names[0];
        }
        return '';
    }

    checkUserPlan(): void {
        this.paymentsService.hasPaid()
            .pipe(
                takeUntil(this.destroy$),
                catchError(error => {
                    console.error('Error checking payment status:', error);
                    this.userPlan = 'Plan Invitado';
                    return of({ has_paid: false });
                })
            )
            .subscribe(response => {
                // Verificar si la respuesta tiene la propiedad has_paid
                const hasPaid = response && response.has_paid === true;
                this.userPlan = hasPaid ? 'Plan Aprende' : 'Plan Invitado';
                console.log('Plan actualizado:', this.userPlan, 'Has paid:', hasPaid);
            });
    }

    onConfigurationClick(): void {
        this.closeProfileMenu();
        // TODO: Implementar navegación a página de configuración
        console.log('Navegando a configuración...');
    }

    onLogoutFromProfile(): void {
        this.closeProfileMenu();
        this.onLogoutClick();
        
    }

    // Cerrar menú de perfil al hacer clic fuera
    @HostListener('document:click', ['$event'])
    onDocumentClick(event: Event): void {
        const target = event.target as HTMLElement;
        const profileContainer = target.closest('.profile-container');
        
        if (!profileContainer && this.showProfileMenu) {
            this.closeProfileMenu();
        }
    }

    // Manejar clic en el navbar-brand (logo)
    onNavbarBrandClick(): void {
        if (this.isAuthenticated) {
            // Si está autenticado, redirigir al menú principal
            this.router.navigate(['/principal-menu']);
        } else {
            // Si no está autenticado, hacer scroll a la sección home
            this.scrollToSection('home');
        }
    }
    tuEstudio(){
        this.closeProfileMenu();
        this.router.navigate(['/principal-menu']);
    }
    ayuda(){
        this.closeProfileMenu();
        this.router.navigate(['/support']);
    }
    scrollToSection(sectionId: string) {
        if (this.isScrolling) return; // Evitar múltiples desplazamientos simultáneos
        
        // Cerrar el menú móvil
        this.closeMobileMenu();
        
        // Verificar si estamos en la página principal
        const isOnHomePage = this.router.url === '/' || this.router.url === '/home' || this.router.url.startsWith('/#');
        
        if (!isOnHomePage) {
            // Si no estamos en la página principal, navegar primero a la raíz con el fragmento
            this.router.navigate(['/'], { fragment: sectionId }).then(() => {
                // Esperar un momento para que la página cargue
                setTimeout(() => {
                    this.performScroll(sectionId);
                }, 100);
            });
        } else {
            // Si ya estamos en la página principal, hacer scroll directamente
            this.performScroll(sectionId);
        }
    }

    private performScroll(sectionId: string) {
        const element = document.getElementById(sectionId);
        if (element) {
            this.isScrolling = true;
            const targetPosition = element.offsetTop - 80; // Offset para el navbar fijo
            const startPosition = window.pageYOffset;
            const distance = targetPosition - startPosition;
            const duration = 1500; // Duración en milisegundos
            let start: number | null = null;

            // Función de easing para movimiento más natural
            const easeInOutCubic = (t: number): number => {
                return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
            };

            // Función de easing alternativo más suave
            const easeOutQuart = (t: number): number => {
                return 1 - Math.pow(1 - t, 4);
            };

            const animation = (currentTime: number) => {
                if (start === null) start = currentTime;
                const timeElapsed = currentTime - start;
                const progress = Math.min(timeElapsed / duration, 1);
                const easedProgress = easeOutQuart(progress);
                
                window.scrollTo(0, startPosition + distance * easedProgress);
                
                if (timeElapsed < duration) {
                    requestAnimationFrame(animation);
                } else {
                    this.isScrolling = false;
                    // Scroll final para asegurar posición exacta
                    window.scrollTo(0, targetPosition);
                }
            };

            requestAnimationFrame(animation);
        }
    }

    closeMobileMenu() {
        // Obtener el elemento del menú colapsable
        const navbarCollapse = document.getElementById('navbarSupportedContent');
        if (navbarCollapse && navbarCollapse.classList.contains('show')) {
            // Crear y disparar el evento de clic en el botón del navbar
            const navbarToggler = document.querySelector('.navbar-toggler') as HTMLElement;
            if (navbarToggler) {
                navbarToggler.click();
            }
        }
    }

    recallJsFuntions() {
        this.routerSubscription = this.router.events
        .pipe(filter(event => event instanceof NavigationEnd || event instanceof NavigationCancel))
        .subscribe(event => {
            this.location = this.router.url;
        });
    }

    gotoSoporte(): void {
        this.router.navigate(['/course/support']);
    }

    gotoAprendizaje(): void {
        this.router.navigate(['/course/principal-menu'])
    }
    
    appLandingNavItem = [
        {
            id: 'home',
            title: 'APRENDE',
        },
        {
            id: 'about',
            title: 'MI ESTUDIO',
        },
        {
            id: 'pricing',
            title: 'RECURSOS',
        },
        {
            id: 'screenshots',
            title: 'BLOG',
        },
        {
            id: 'team',
            title: 'NOSOTROS',
        },
        {
            id: 'faq',
            title: 'iNICIO SESIÓN',
        },
        {
            id: 'feedback',
            title: 'Feedback',
        },
        {
            id: 'contact',
            title: 'Contact',
        }
    ];
    bookLandingNavItem = [
        {
            id: 'home',
            title: 'Home',
        },
        {
            id: 'features',
            title: 'Features',
        },
        {
            id: 'about',
            title: 'About',
        },
        {
            id: 'feedback',
            title: 'Feedback',
        },
        {
            id: 'pricing',
            title: 'Pricing',
        },
        {
            id: 'author',
            title: 'Author',
        },
        {
            id: 'contact',
            title: 'Contact',
        }
    ];
    saasLandingNavItem = [
        {
            id: 'home',
            title: 'Home',
        },
        {
            id: 'about',
            title: 'About',
        },
        {
            id: 'features',
            title: 'Features',
        },
        {
            id: 'pricing',
            title: 'Pricing',
        },
        {
            id: 'team',
            title: 'Team',
        },
        {
            id: 'feedback',
            title: 'Feedback',
        },
        {
            id: 'contact',
            title: 'Contact',
        }
    ];
    productsLandingNavItem = [
        {
            id: 'home',
            title: 'Home',
        },
        {
            id: 'about',
            title: 'About',
        },
        {
            id: 'features',
            title: 'Features',
        },
        {
            id: 'spec',
            title: 'Spec',
        },
        {
            id: 'products',
            title: 'Products',
        },
        {
            id: 'faq',
            title: 'FAQ',
        },
        {
            id: 'contact',
            title: 'Contact',
        }
    ];

}